import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithCustomToken, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, collection, onSnapshot } from 'firebase/firestore';

import { 
  MessageCircle, 
  LayoutTemplate, 
  MousePointer2, 
  Mic, 
  MicOff,
  Settings, 
  ChevronRight, 
  Edit3, 
  Save, 
  CheckCircle2,
  TrendingUp,
  Clock,
  ShieldCheck,
  Zap,
  ChevronDown,
  LayoutDashboard,
  Users,
  ShoppingCart,
  ArrowLeft,
  Menu,
  ShoppingBag,
  Heart,
  Volume2,
  Plus,
  Video,
  PhoneOff,
  Layout,
  Type,
  List,
  CreditCard,
  PanelTop,
  PanelBottom,
  FileText,
  X,
  Lock
} from 'lucide-react';

// --- FIREBASE INITIALIZATION ---
let app, auth, db, appId;
try {
  const firebaseConfig = {
    apiKey: "AIzaSyAp_hZQzpBLetrmFd_p1rBZ0TSWKNd6Sv8",
    authDomain: "shopify-canvas.firebaseapp.com",
    projectId: "shopify-canvas",
    storageBucket: "shopify-canvas.firebasestorage.app",
    messagingSenderId: "761105505192",
    appId: "1:761105505192:web:22628995b734ddd43b9b9d",
    measurementId: "G-531B8T7F2N"
  };

  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  
  appId = "shopify-canvas"; 
} catch (e) {
  console.warn("Firebase configuration error. Check your credentials.", e);
}

// --- INITIAL CMS DATA ---
const initialData = {
  header: {
    logo: "Canvas Future.",
    logoImage: "",
    nav: ["ROI Calculator", "Pricing"]
  },
  hero: {
    badge: "Next-Gen Shopify Solutions",
    title: "Scale Your Store With Agentic AI & Liquid Glass Design",
    subtitle: "Unlock untapped revenue with WhatsApp cart recovery, intelligent heatmaps, voice bots, and hyper-converting liquid themes.",
    cta1: "Start Free Trial",
    cta2: "Watch Demo"
  },
  solutions: [
    {
      id: "whatsapp",
      icon: "MessageCircle",
      tabTitle: "WhatsApp Recovery",
      title: "Agentic WhatsApp Cart Recovery",
      description: "Don't let abandoned carts sit. Our Agentic AI initiates hyper-personalized WhatsApp conversations, negotiating discounts and closing sales automatically.",
      features: ["Human-like negotiation", "Automated checkout links", "15%+ average recovery rate"]
    },
    {
      id: "themes",
      icon: "LayoutTemplate",
      tabTitle: "Liquid JS Themes",
      title: "Premium Liquid JS Themes",
      description: "Upgrade your Shopify store with our high-converting, blazing-fast themes designed with future-proof aesthetics and zero layout shifts.",
      features: ["Sub-second load times", "Built-in upsell blocks", "Mobile-first responsive design"]
    },
    {
      id: "heatmaps",
      icon: "MousePointer2",
      tabTitle: "AI Heatmaps",
      title: "Live Session & AI Heatmaps",
      description: "Record visitor sessions and let our AI analyze heatmaps to pinpoint exact drop-off points. Understand exactly why users aren't converting.",
      features: ["Rage-click detection", "Scroll depth analysis", "Automated UX suggestions"]
    },
    {
      id: "voicebot",
      icon: "Mic",
      tabTitle: "Voice AI Bots",
      title: "Agentic Voice Support",
      description: "Deploy 24/7 AI voice agents to handle order verification, shipping inquiries, and returns without human intervention.",
      features: ["Natural language processing", "Instant order lookup", "Multilingual support"]
    }
  ],
  innerPages: {
    whatsapp: {
      title: "Recover 15%+ of Abandoned Carts on WhatsApp",
      description: "Email cart recovery is dead. Engage your customers where they actually spend their time. Our Agentic AI acts as your best sales rep, talking directly to high-intent buyers.",
      features: [
        { title: "Human-like Conversational AI", desc: "Our AI doesn't sound like a bot. It understands context, negotiates, answers product queries, and closes the sale." },
        { title: "Instant Checkout Links", desc: "Generates secure, pre-filled Shopify checkout links with discounts automatically applied for frictionless purchasing." },
        { title: "Analytics & ROI Tracking", desc: "A comprehensive dashboard to see exactly how many carts were recovered, response rates, and total revenue generated." }
      ]
    },
    themes: {
      title: "High-Converting Liquid JS Shopify Themes",
      description: "Aesthetics meet raw performance. Our custom Liquid Themes are built for the modern brand that refuses to compromise on speed or design.",
      features: [
        { title: "Zero Layout Shift", desc: "Optimized Core Web Vitals right out of the box ensuring perfect Google Lighthouse scores." },
        { title: "Immersive Interactions", desc: "Integrated WebGL and sophisticated CSS animations that engage users without bloating load times." },
        { title: "Conversion-Optimized Layouts", desc: "Built-in upsells, cross-sells, sticky Add-To-Cart buttons, and frictionless checkout flows." }
      ]
    },
    heatmaps: {
      title: "Uncover The 'Why' Behind Lost Sales",
      description: "Stop guessing why your conversion rate is dropping. Our intelligent heatmaps and session recordings show you exactly how users interact with your store.",
      features: [
        { title: "Rage Click & Dead Click Tracking", desc: "Identify broken buttons and confusing UI elements that cause immediate user frustration." },
        { title: "AI-Powered UX Insights", desc: "Our agent analyzes thousands of sessions and automatically suggests layout improvements to boost conversions." },
        { title: "Privacy-First Recordings", desc: "Capture user behavior perfectly while automatically masking sensitive personal data like passwords and credit cards." }
      ]
    },
    voicebot: {
      title: "24/7 Agentic Voice Support For Shopify",
      description: "Eliminate your support backlog. Deploy intelligent Voice AI that picks up the phone instantly, processes refunds, and tracks orders by integrating directly with your Shopify backend.",
      features: [
        { title: "Real-time Shopify Integration", desc: "The bot instantly looks up order status, shipping times, and tracking numbers using the customer's phone number." },
        { title: "Action-Oriented Intent", desc: "Can automatically process simple returns, issue store credit, or escalate complex issues to human agents with full transcripts." },
        { title: "Multilingual Capabilities", desc: "Seamlessly support global customers in over 30 languages with native-sounding accents." }
      ]
    }
  },
  pricing: {
    whatsapp: [
      { name: "Starter", price: "₹5,999", desc: "Perfect for new stores.", features: ["Basic WA Recovery", "1,000 Messages/mo", "Standard Analytics"] },
      { name: "Growth", price: "₹14,499", desc: "For scaling brands.", features: ["Advanced Agentic WA", "Unlimited Messages", "Custom Chatbots", "Priority Support"] },
      { name: "Enterprise", price: "Custom", desc: "High-volume dominance.", features: ["Dedicated AI Models", "Full Integration", "SLA Guarantee", "24/7 Support"] }
    ],
    themes: [
      { name: "Single Store", price: "₹7,999", desc: "One-time license.", features: ["1 Store License", "6 Months Support", "Lifetime Updates"] },
      { name: "Developer", price: "₹24,999", desc: "Build for clients.", features: ["3 Store Licenses", "1 Year Support", "Figma Files", "Priority Updates"] },
      { name: "Agency", price: "Custom", desc: "Unlimited power.", features: ["Unlimited Stores", "Dedicated Manager", "Custom Dev Support", "White-label"] }
    ],
    heatmaps: [
      { name: "Basic", price: "₹12,999", desc: "Understand your traffic.", features: ["1,000 Sessions/mo", "Rage Click Detection", "Basic Heatmaps"] },
      { name: "Pro", price: "₹29,999", desc: "Deep UX insights.", features: ["5,000 Sessions/mo", "AI UX Suggestions", "Live Replays", "Export Data"] },
      { name: "Enterprise", price: "Custom", desc: "For massive stores.", features: ["Unlimited Sessions", "Custom Dashboards", "Raw Data Access", "SLA Guarantee"] }
    ],
    voicebot: [
      { name: "Starter", price: "₹9,999", desc: "Handle basic queries.", features: ["500 Calls/mo", "Standard Voice", "Order Lookup"] },
      { name: "Growth", price: "₹24,999", desc: "Full automation.", features: ["2,000 Calls/mo", "Custom Voice Cloning", "Returns Processing", "Multilingual"] },
      { name: "Enterprise", price: "Custom", desc: "Replace call centers.", features: ["Unlimited Calls", "Dedicated Server", "Custom Integrations", "SLA Guarantee"] }
    ]
  },
  footer: {
    text: "© 2026 Canvas Future. All rights reserved. Empowering Shopify merchants globally."
  }
};

// --- UTILITY COMPONENTS ---

const GlassBox = ({ children, className = "" }) => (
  <div className={`bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.15)] rounded-2xl ${className}`}>
    {children}
  </div>
);

const IconRenderer = ({ name, className }) => {
  const icons = { MessageCircle, LayoutTemplate, MousePointer2, Mic, Settings, TrendingUp, Clock, ShieldCheck, Zap };
  const Icon = icons[name] || Settings;
  return <Icon className={className} />;
};

const EText = ({ data, path, tag: Tag = 'span', className = "" }) => {
  const getValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);
  const value = getValue(data, path) || "";
  return <Tag className={className}>{value}</Tag>;
};

// --- FULL PAGE CMS EDITOR COMPONENT ---

const CMSEditor = ({ initialData, onPublish, onClose }) => {
  const [localData, setLocalData] = useState(() => JSON.parse(JSON.stringify(initialData)));
  const [activeSection, setActiveSection] = useState('header');
  const [cmsPricingTab, setCmsPricingTab] = useState('whatsapp');

  const sections = [
    { id: 'header', label: 'Header Menu', icon: PanelTop },
    { id: 'hero', label: 'Hero Section', icon: Layout },
    { id: 'solutions', label: 'Solutions (Home)', icon: List },
    { id: 'innerPages', label: 'Inner Pages', icon: FileText },
    { id: 'pricing', label: 'Pricing Plans', icon: CreditCard },
    { id: 'footer', label: 'Footer', icon: PanelBottom }
  ];

  const handleChange = (path, newValue) => {
    setLocalData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = newValue;
      return newData;
    });
  };

  const Field = ({ label, path, isTextArea = false }) => {
    const value = path.split('.').reduce((acc, part) => acc && acc[part], localData) || "";
    return (
      <div className="mb-5">
        <label className="block text-xs font-bold text-blue-400 mb-2 uppercase tracking-wider">{label}</label>
        {isTextArea ? (
          <textarea 
            rows={4} 
            value={value} 
            onChange={e => handleChange(path, e.target.value)} 
            className="w-full bg-[#0b0c10] border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none transition-colors shadow-inner" 
          />
        ) : (
          <input 
            type="text" 
            value={value} 
            onChange={e => handleChange(path, e.target.value)} 
            className="w-full bg-[#0b0c10] border border-white/10 rounded-xl p-4 text-white focus:border-blue-500 outline-none transition-colors shadow-inner" 
          />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#05050A] text-white flex overflow-hidden font-sans" style={{ fontFamily: "'Lexend', sans-serif" }}>
      {/* CMS Sidebar */}
      <div className="w-72 bg-[#0b0c10] border-r border-white/5 flex flex-col shrink-0">
        <div className="p-6 border-b border-white/5 relative">
          <button onClick={onClose} className="absolute top-6 right-6 text-white/50 hover:text-white"><X size={18}/></button>
          <div className="flex items-center gap-3 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
            <Settings size={24} className="text-blue-400" /> Canvas CMS
          </div>
          <p className="text-white/40 text-xs mt-2 font-medium">Full Page Block Editor</p>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4">
          {sections.map(section => (
            <button 
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full flex items-center gap-3 px-6 py-4 text-sm font-semibold transition-all border-l-2 ${
                activeSection === section.id 
                  ? 'bg-blue-600/10 border-blue-500 text-blue-400' 
                  : 'border-transparent text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <section.icon size={18} /> {section.label}
            </button>
          ))}
        </div>

        <div className="p-6 border-t border-white/5 bg-gradient-to-t from-black/50 to-transparent">
          <button 
            onClick={() => onPublish(localData)}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold hover:shadow-[0_0_20px_rgba(79,70,229,0.5)] transition-all"
          >
            <Save size={18} /> Publish & Exit
          </button>
        </div>
      </div>

      {/* CMS Main Content */}
      <div className="flex-1 overflow-y-auto bg-[#05050A] p-10 relative scrollbar-hide">
        <div className="max-w-4xl mx-auto">
          
          {/* Header Editor */}
          {activeSection === 'header' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><PanelTop className="text-blue-500"/> Header Configuration</h2>
              <GlassBox className="p-8 mb-8">
                <Field label="Logo Image URL (Optional)" path="header.logoImage" />
                <Field label="Brand Logo Text (Fallback)" path="header.logo" />
              </GlassBox>
              <GlassBox className="p-8">
                <h3 className="text-lg font-semibold mb-4 text-white/80">Navigation Links</h3>
                <Field label="Link 1" path="header.nav.0" />
                <Field label="Link 2" path="header.nav.1" />
              </GlassBox>
            </div>
          )}

          {/* Hero Editor */}
          {activeSection === 'hero' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><Layout className="text-blue-500"/> Hero Section</h2>
              <GlassBox className="p-8">
                <Field label="Badge Text" path="hero.badge" />
                <Field label="Main Headline" path="hero.title" isTextArea />
                <Field label="Subheadline" path="hero.subtitle" isTextArea />
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <Field label="Primary CTA Button" path="hero.cta1" />
                  <Field label="Secondary CTA Button" path="hero.cta2" />
                </div>
              </GlassBox>
            </div>
          )}

          {/* Solutions (Home) Editor */}
          {activeSection === 'solutions' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><List className="text-blue-500"/> Core Solutions (Homepage Tabs)</h2>
              {localData.solutions.map((sol, index) => (
                <GlassBox key={sol.id} className="p-8 mb-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                    <div className="p-2 rounded bg-blue-500/20 text-blue-400"><IconRenderer name={sol.icon} className="w-5 h-5"/></div>
                    <h3 className="text-xl font-bold text-white">{sol.tabTitle} Block</h3>
                  </div>
                  <Field label="Navigation Tab Title" path={`solutions.${index}.tabTitle`} />
                  <Field label="Main Title" path={`solutions.${index}.title`} />
                  <Field label="Description" path={`solutions.${index}.description`} isTextArea />
                  <div className="mt-6">
                    <label className="block text-xs font-bold text-white/50 mb-4 uppercase tracking-wider">Bullet Features</label>
                    <div className="space-y-4">
                      <Field label="Feature 1" path={`solutions.${index}.features.0`} />
                      <Field label="Feature 2" path={`solutions.${index}.features.1`} />
                      <Field label="Feature 3" path={`solutions.${index}.features.2`} />
                    </div>
                  </div>
                </GlassBox>
              ))}
            </div>
          )}

          {/* Inner Pages Editor */}
          {activeSection === 'innerPages' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><FileText className="text-blue-500"/> Inner Service Pages</h2>
              {Object.keys(localData.innerPages).map((pageKey) => (
                <GlassBox key={pageKey} className="p-8 mb-8">
                  <h3 className="text-xl font-bold text-white mb-6 pb-4 border-b border-white/10 capitalize">{pageKey} Full Page</h3>
                  <Field label="Page Headline" path={`innerPages.${pageKey}.title`} isTextArea />
                  <Field label="Detailed Description" path={`innerPages.${pageKey}.description`} isTextArea />
                  <div className="mt-8 space-y-8">
                    <label className="block text-xs font-bold text-white/50 uppercase tracking-wider">Detailed Feature Blocks</label>
                    {[0, 1, 2].map((featIdx) => (
                      <div key={featIdx} className="bg-white/5 p-6 rounded-xl border border-white/5">
                        <Field label={`Feature ${featIdx + 1} Title`} path={`innerPages.${pageKey}.features.${featIdx}.title`} />
                        <Field label={`Feature ${featIdx + 1} Description`} path={`innerPages.${pageKey}.features.${featIdx}.desc`} isTextArea />
                      </div>
                    ))}
                  </div>
                </GlassBox>
              ))}
            </div>
          )}

          {/* Pricing Editor */}
          {activeSection === 'pricing' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><CreditCard className="text-blue-500"/> Pricing Plans</h2>
              
              <div className="flex flex-wrap gap-2 mb-8 bg-black/50 p-2 rounded-xl border border-white/10 w-max">
                {localData.solutions.map((sol) => (
                  <button 
                    key={sol.id}
                    onClick={() => setCmsPricingTab(sol.id)}
                    className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors ${cmsPricingTab === sol.id ? 'bg-blue-600 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
                  >
                    {sol.tabTitle}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {localData.pricing[cmsPricingTab].map((plan, index) => (
                  <GlassBox key={`${cmsPricingTab}-${index}`} className="p-6">
                    <h3 className="text-lg font-bold text-white mb-6 pb-4 border-b border-white/10">Tier {index + 1}</h3>
                    <Field label="Plan Name" path={`pricing.${cmsPricingTab}.${index}.name`} />
                    <Field label="Price" path={`pricing.${cmsPricingTab}.${index}.price`} />
                    <Field label="Short Description" path={`pricing.${cmsPricingTab}.${index}.desc`} isTextArea />
                    <div className="mt-4">
                      <label className="block text-xs font-bold text-white/50 mb-4 uppercase tracking-wider">Features</label>
                      <Field label="Feat 1" path={`pricing.${cmsPricingTab}.${index}.features.0`} />
                      <Field label="Feat 2" path={`pricing.${cmsPricingTab}.${index}.features.1`} />
                      <Field label="Feat 3" path={`pricing.${cmsPricingTab}.${index}.features.2`} />
                      {plan.features[3] !== undefined && <Field label="Feat 4" path={`pricing.${cmsPricingTab}.${index}.features.3`} />}
                    </div>
                  </GlassBox>
                ))}
              </div>
            </div>
          )}

          {/* Footer Editor */}
          {activeSection === 'footer' && (
            <div className="animate-[fade-in-up_0.3s_ease-out]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3"><PanelBottom className="text-blue-500"/> Footer Settings</h2>
              <GlassBox className="p-8">
                <Field label="Copyright & Footer Text" path="footer.text" />
              </GlassBox>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};


// --- MOCKUP COMPONENTS ---

const DashboardMockup = () => (
  <div className="w-full max-w-2xl bg-[#0b0c10] rounded-2xl border border-white/10 shadow-[0_0_50px_rgba(37,99,235,0.2)] flex overflow-hidden transform rotate-[2deg] hover:rotate-0 transition-all duration-500 relative">
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.05] to-transparent pointer-events-none z-20"></div>
    <div className="w-48 bg-[#111318] border-r border-white/5 p-4 hidden md:flex flex-col gap-4 z-10">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded bg-gradient-to-br from-green-400 to-green-600"></div>
        <div className="text-white/80 font-bold text-sm">Store Admin</div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 px-3 py-2 bg-blue-600/20 text-blue-400 rounded-lg text-sm font-medium border border-blue-500/20">
          <LayoutDashboard size={16}/> Home
        </div>
        <div className="flex items-center gap-3 px-3 py-2 text-white/50 hover:text-white/80 text-sm font-medium">
          <ShoppingCart size={16}/> Orders
        </div>
        <div className="flex items-center gap-3 px-3 py-2 text-white/50 hover:text-white/80 text-sm font-medium">
          <Users size={16}/> Customers
        </div>
      </div>
      <div className="mt-auto">
        <div className="h-20 bg-gradient-to-t from-blue-900/40 to-transparent rounded-lg border border-blue-500/20 p-3 flex flex-col justify-end">
           <div className="text-blue-300 text-xs font-semibold mb-1">Canvas AI Agent</div>
           <div className="text-green-400 text-xs flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> Active</div>
        </div>
      </div>
    </div>
    <div className="flex-1 p-6 flex flex-col gap-6 bg-[#05050A] z-10 relative">
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex justify-between items-end">
        <div>
          <div className="text-white/50 text-sm mb-1">Total Sales (Today)</div>
          <div className="text-3xl font-bold text-white flex items-center gap-3">
            ₹1,42,500.00
            <span className="text-sm font-medium text-green-400 bg-green-400/10 px-2 py-0.5 rounded flex items-center gap-1">
              <TrendingUp size={14}/> +24%
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 border border-white/5 rounded-xl p-4">
          <div className="text-white/50 text-xs mb-2">Cart Recovery Rate</div>
          <div className="text-xl font-semibold text-blue-400">18.4%</div>
          <div className="w-full bg-black/50 h-1.5 rounded-full mt-3 overflow-hidden">
             <div className="bg-blue-500 w-[18.4%] h-full rounded-full shadow-[0_0_10px_#3b82f6]"></div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/5 rounded-xl p-4">
          <div className="text-white/50 text-xs mb-2">Active AI Support Chats</div>
          <div className="text-xl font-semibold text-purple-400">42</div>
          <div className="flex gap-1 mt-3">
            {[...Array(5)].map((_,i) => <div key={i} className="flex-1 bg-purple-500/20 h-1.5 rounded-full overflow-hidden">
               <div className="bg-purple-400 h-full w-full animate-pulse" style={{ animationDelay: `${i*0.2}s`}}></div>
            </div>)}
          </div>
        </div>
      </div>
      <div className="flex-1 bg-white/5 border border-white/5 rounded-xl relative overflow-hidden flex flex-col justify-end min-h-[150px]">
        <div className="absolute top-4 left-4 text-white/50 text-xs">Revenue Trend</div>
        <svg viewBox="0 0 100 50" className="w-full h-full mt-8 drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.4)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
            </linearGradient>
          </defs>
          <path d="M0,50 L0,30 C15,35 25,15 40,25 C55,35 65,10 80,15 C90,18 95,5 100,5 L100,50 Z" fill="url(#grad)" />
          <path d="M0,30 C15,35 25,15 40,25 C55,35 65,10 80,15 C90,18 95,5 100,5" fill="none" stroke="#3b82f6" strokeWidth="2" className="animate-[dash_3s_linear_forwards]" strokeDasharray="200" strokeDashoffset="0" />
        </svg>
      </div>
    </div>
  </div>
);

const PhoneFrame = ({ children, className="" }) => (
  <div className={`relative w-[300px] h-[600px] rounded-[3rem] border-[12px] border-[#1a1a24] bg-[#0A0A12] shadow-[0_0_50px_rgba(0,82,255,0.3)] overflow-hidden shrink-0 flex flex-col ${className}`}>
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-[#1a1a24] rounded-b-3xl z-50 flex items-center justify-center gap-2">
      <div className="w-2 h-2 rounded-full bg-white/10"></div>
      <div className="w-12 h-2 rounded-full bg-white/10"></div>
    </div>
    <div className="w-full h-full relative flex-1 flex flex-col">
      {children}
    </div>
  </div>
);

const BrowserFrame = ({ children, className="" }) => (
  <div className={`relative w-[350px] sm:w-[450px] lg:w-[550px] h-[400px] lg:h-[450px] rounded-xl border border-white/10 bg-[#0A0A12] shadow-[0_0_50px_rgba(0,82,255,0.3)] overflow-hidden shrink-0 flex flex-col ${className}`}>
    <div className="h-8 bg-[#1a1a24] border-b border-white/10 flex items-center px-3 gap-2 shrink-0">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
      </div>
      <div className="mx-auto bg-[#0A0A12] border border-white/5 rounded text-[10px] text-white/40 px-8 sm:px-24 py-0.5 flex items-center justify-center truncate max-w-[60%]">
         canvas-aura.myshopify.com
      </div>
    </div>
    <div className="w-full flex-1 relative overflow-hidden bg-[#05050A]">
      {children}
    </div>
  </div>
);

const WhatsAppScreen = () => (
  <div className="flex flex-col h-full bg-[#0b141a]">
    <div className="bg-[#202c33] pt-10 pb-3 px-4 flex items-center gap-3 border-b border-[#111b21] z-10 shadow-sm">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shrink-0 relative overflow-hidden">
        <Zap size={20} className="text-white" />
      </div>
      <div className="flex-1">
        <div className="text-white text-[15px] font-semibold flex items-center gap-1.5 leading-tight">
          Canvas Store <div className="bg-[#00a884] rounded-full p-[2px]"><CheckCircle2 size={10} className="text-[#202c33]" strokeWidth={4} /></div>
        </div>
        <div className="text-[#8696a0] text-xs">Official Business Account</div>
      </div>
    </div>

    <div className="flex-1 p-4 flex flex-col gap-3 overflow-hidden text-[13.5px] relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.04] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxwYXRoIGQ9Ik0wIDBoMjB2MjBIMHoiIGZpbGw9Im5vbmUiLz4KPGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9IiNmZmYiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMSIgZmlsbD0iI2ZmZiIvPgo8L3N2Zz4=')]"></div>
      
      {/* Meta Button Message */}
      <div className="flex flex-col self-start max-w-[88%] animate-[fade-in-up_0.5s_ease-out_both] z-10 drop-shadow-sm">
        <div className="bg-[#202c33] rounded-xl rounded-tl-none overflow-hidden border border-white/5">
          <div className="w-full h-32 bg-[#111b21] relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 to-black flex flex-col items-center justify-center border-b border-white/5">
              <ShoppingBag size={36} className="text-white/60 mb-2 drop-shadow-lg" />
              <span className="text-white/80 font-bold text-xs tracking-widest uppercase">Your Cart</span>
            </div>
          </div>
          <div className="p-3 pb-2 text-white">
            <p className="font-bold mb-1.5 text-[14px]">You left something behind! 🛒</p>
            <p className="text-[#e9edef] leading-snug">Hi Sarah, your <span className="font-semibold text-white">Liquid Drop Hoodie</span> is waiting. Complete your purchase now and get <span className="font-bold text-[#00a884]">15% OFF</span>!</p>
            <div className="flex justify-end mt-1">
              <span className="text-[#8696a0] text-[10px]">10:42 AM</span>
            </div>
          </div>
          {/* WhatsApp Native Buttons */}
          <div className="flex flex-col border-t border-white/10 bg-[#202c33]">
            <div className="p-3 text-center text-[#53bdeb] font-semibold border-b border-white/10 cursor-pointer hover:bg-white/5 active:bg-white/10 transition-colors flex items-center justify-center gap-2">
              <ShoppingCart size={16} /> Complete Purchase
            </div>
            <div className="p-3 text-center text-[#53bdeb] font-semibold cursor-pointer hover:bg-white/5 active:bg-white/10 transition-colors">
              View Cart
            </div>
          </div>
        </div>
      </div>

      {/* User Click Simulation */}
      <div className="bg-[#005c4b] text-[#e9edef] p-2 px-3 rounded-lg rounded-tr-none self-end max-w-[85%] animate-[fade-in-up_1.5s_ease-out_both] z-10 shadow-sm mt-1 flex items-end gap-2">
        <span>Complete Purchase</span>
        <div className="flex items-center gap-1 shrink-0 mb-[2px]">
          <span className="text-[#8696a0] text-[10px]">10:45 AM</span>
          <svg viewBox="0 0 16 11" width="16" height="11" className="text-[#53bdeb] fill-current"><path d="M11.8 1.4L10.4 0 5.1 5.3 2.5 2.7 1.1 4.1l4 4 1.4 1.4 6.7-6.7zM16 1.4L14.6 0l-4.1 4.1 1.4 1.4L16 1.4zM4.1 8.1l-1.4-1.4L0 9.4 1.4 10.8l2.7-2.7z"></path></svg>
        </div>
      </div>

      {/* Follow up */}
      <div className="bg-[#202c33] text-[#e9edef] p-2.5 px-3 rounded-lg rounded-tl-none self-start max-w-[88%] animate-[fade-in-up_2.5s_ease-out_both] z-10 shadow-sm mt-1">
        Awesome! 🎉 Here's your secure link with the code <span className="font-bold bg-white/10 px-1 rounded text-[#00a884] text-xs">SAVE15</span> applied: <br/><br/><span className="text-[#53bdeb] underline break-all cursor-pointer">https://canvas.store/chk/8f2a</span>
        <div className="flex justify-end mt-1">
          <span className="text-[#8696a0] text-[10px]">10:45 AM</span>
        </div>
      </div>
    </div>

    <div className="bg-[#202c33] p-2.5 px-3 flex gap-3 items-center pb-6 z-10">
      <Plus size={24} className="text-[#8696a0] shrink-0" />
      <div className="flex-1 bg-[#2a3942] rounded-full h-10 px-4 flex items-center text-[#8696a0] text-[15px]">Message</div>
      <div className="w-10 h-10 bg-[#00a884] rounded-full flex items-center justify-center shrink-0">
        <Mic size={20} className="text-[#111b21]" />
      </div>
    </div>
  </div>
);

const ThemeScreen = () => (
  <div className="h-full w-full bg-[#05050A] text-white overflow-hidden relative flex flex-col group">
    <div className="w-full h-max animate-[scroll-up-down_15s_ease-in-out_infinite] group-hover:[animation-play-state:paused]">
      <div className="bg-blue-600 text-[10px] font-bold text-center py-1.5 tracking-[0.2em] uppercase">
        Free Global Shipping on orders over $150
      </div>
      <div className="sticky top-0 z-50 bg-[#05050A]/80 backdrop-blur-md border-b border-white/10 px-6 py-4 flex justify-between items-center">
        <div className="text-lg font-black tracking-widest uppercase">AURA.</div>
        <div className="hidden sm:flex items-center gap-6 text-[11px] font-medium text-white/70">
           <span className="hover:text-white cursor-pointer">Shop</span>
           <span className="hover:text-white cursor-pointer">Collections</span>
           <span className="hover:text-white cursor-pointer">About</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[11px] text-white/70 hidden sm:block cursor-pointer">Search</span>
          <div className="relative cursor-pointer">
            <ShoppingBag size={18} className="text-white/80" />
            <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-500 rounded-full text-[9px] flex items-center justify-center font-bold border border-[#05050A]">2</div>
          </div>
        </div>
      </div>
      <div className="relative h-[220px] sm:h-[260px] flex flex-col items-center justify-center overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#05050A] via-[#0a0f1c] to-blue-900/40"></div>
        <div className="absolute inset-0 opacity-40 mix-blend-screen bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/30 via-transparent to-transparent blur-2xl animate-pulse"></div>
        <div className="relative z-10 text-center px-4 w-full flex flex-col items-center">
          <div className="text-blue-400 text-[10px] font-bold tracking-widest uppercase mb-3">New Arrival</div>
          <h2 className="text-4xl sm:text-5xl font-black italic tracking-tighter mb-3 leading-none">LIQUID DROP '26</h2>
          <p className="text-[10px] sm:text-xs text-white/60 mb-6 max-w-sm">Experience zero layout shift and sub-second load times.</p>
          <button className="bg-white text-black text-xs font-bold px-8 py-2.5 sm:py-3 rounded-full hover:bg-blue-500 hover:text-white transition-all shadow-[0_0_15px_rgba(255,255,255,0.2)]">
            SHOP NOW
          </button>
        </div>
      </div>
      <div className="p-6">
        <div className="flex justify-between items-end mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wider">Trending Now</h3>
          <span className="text-[10px] text-blue-400 cursor-pointer hover:underline">View All</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pb-12">
          {[
            { name: 'Velocity Jacket', price: '$120.00', tag: 'Bestseller' },
            { name: 'Aura Tech Tee', price: '$45.00', tag: '' },
            { name: 'Liquid Denim', price: '$85.00', tag: 'New' }
          ].map((prod, i) => (
            <div key={i} className={`flex flex-col group/card cursor-pointer ${i === 2 ? 'hidden sm:flex' : ''}`}>
              <div className="w-full aspect-[4/5] bg-[#0c0c12] rounded-xl mb-3 relative overflow-hidden group-hover/card:border-blue-500/50 border border-white/5 transition-colors">
                <div className={`absolute inset-0 bg-gradient-to-tr ${i%2===0 ? 'from-blue-900/20 to-[#0c0c12]' : 'from-cyan-900/20 to-[#0c0c12]'}`}></div>
                {prod.tag && (
                  <div className="absolute top-2 left-2 bg-white text-black text-[8px] font-bold px-2 py-0.5 rounded-sm uppercase tracking-wider">
                    {prod.tag}
                  </div>
                )}
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center border border-white/10 hover:bg-white/20 transition-colors">
                  <Heart size={10} className="text-white/80" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-2 translate-y-full group-hover/card:translate-y-0 transition-transform duration-300">
                  <button className="w-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold py-2 rounded-lg text-white hover:bg-blue-600 hover:border-blue-500 transition-colors">
                    + ADD TO CART
                  </button>
                </div>
              </div>
              <div className="text-[9px] text-white/50 mb-0.5 tracking-wider uppercase">Canvas Originals</div>
              <div className="text-xs font-semibold truncate">{prod.name}</div>
              <div className="text-xs text-blue-400 font-medium mt-0.5">{prod.price}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const HeatmapScreen = () => (
  <div className="h-full w-full bg-[#05050A] overflow-hidden relative font-sans flex flex-col items-center justify-center p-4">
    
    {/* Fake Mobile Screen Being Recorded */}
    <div className="w-full max-w-[240px] h-[380px] bg-white rounded-xl relative overflow-hidden shadow-[0_0_30px_rgba(255,255,255,0.1)] border border-white/20 mt-[-50px]">
       {/* Store Header */}
       <div className="bg-red-600 p-2 text-white text-center font-bold text-[10px] uppercase tracking-widest shadow-md relative z-10">Mega Diwali Sale</div>
       
       {/* Product area */}
       <div className="p-3 flex flex-col gap-2 text-black relative h-full bg-gray-50">
          <div className="w-full h-28 bg-white rounded-lg flex flex-col items-center justify-center border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-gray-200 rounded-full mb-1 border-4 border-gray-100"></div>
            <div className="text-[8px] text-gray-400 font-medium">Product Image</div>
          </div>
          
          <div className="font-bold text-[13px] leading-tight text-gray-800 mt-1">Pro Wireless Earbuds</div>
          <div className="text-base font-black text-green-600">₹1,299 <span className="text-[9px] text-gray-400 line-through font-normal">₹3,999</span></div>

          {/* Pincode Check - Very Indian Context */}
          <div className="mt-1 relative bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
            <div className="text-[9px] font-bold text-gray-700 mb-1.5 flex items-center gap-1 uppercase tracking-wide">
              Check Delivery Pincode
            </div>
            <div className="flex gap-1.5 relative">
              <div className="border border-gray-300 rounded p-1.5 text-[9px] flex-1 text-gray-400 bg-gray-50">Enter Pincode</div>
              <div className="bg-black text-white px-3 py-1.5 rounded text-[9px] font-bold flex items-center justify-center">CHECK</div>
              
              {/* The "Bug" - A fake chat widget covering the check button */}
              <div className="absolute -bottom-4 -right-2 w-9 h-9 bg-[#25D366] rounded-full shadow-xl flex items-center justify-center z-10 border-2 border-white">
                <MessageCircle size={16} className="text-white fill-white" />
              </div>
            </div>
          </div>

          <div className="w-full bg-blue-600 text-white font-bold py-2.5 rounded-lg mt-2 text-[11px] text-center shadow-md">BUY IT NOW</div>
       </div>

       {/* Heatmap Overlay (Red hot spot right over the bug) */}
       <div className="absolute inset-0 pointer-events-none z-20 mix-blend-multiply opacity-90">
          {/* Massive red blob indicating extreme frustration/clicking */}
          <div className="absolute top-[205px] right-1 w-20 h-20 bg-red-500 blur-[15px] rounded-full animate-pulse"></div>
          <div className="absolute top-[215px] right-3 w-10 h-10 bg-yellow-400 blur-[8px] rounded-full"></div>
          <div className="absolute top-[220px] right-5 w-4 h-4 bg-white blur-[2px] rounded-full"></div>
          
          {/* Normal viewing cold spots */}
          <div className="absolute top-10 left-4 w-16 h-16 bg-blue-300/40 blur-[15px] rounded-full"></div>
       </div>

       {/* Mouse Cursor clicking repeatedly on the broken button */}
       <div className="absolute z-30 animate-[frantic-click_4s_infinite] pointer-events-none drop-shadow-lg">
         <MousePointer2 size={18} className="text-black fill-white" />
         {/* Ping rings to show frustrated clicks */}
         <div className="absolute top-0 left-0 w-8 h-8 border-[3px] border-red-600 rounded-full animate-[ping_4s_infinite] opacity-0" style={{animationDelay: '1.2s'}}></div>
         <div className="absolute top-0 left-0 w-8 h-8 border-[3px] border-red-600 rounded-full animate-[ping_4s_infinite] opacity-0" style={{animationDelay: '1.5s'}}></div>
       </div>
    </div>

    {/* Plain English AI Explanation Box */}
    <div className="absolute bottom-5 left-4 right-4 bg-gradient-to-r from-red-950/95 to-red-900/95 backdrop-blur-xl border border-red-500/40 rounded-xl p-3.5 z-40 shadow-[0_10px_30px_rgba(220,38,38,0.2)]">
       <div className="flex items-start gap-3">
         <div className="bg-red-500 rounded-full p-1.5 mt-0.5 shadow-[0_0_15px_rgba(239,68,68,0.6)]"><Zap size={16} className="text-white fill-white" /></div>
         <div>
           <div className="text-white text-[12px] font-bold uppercase tracking-wider mb-1">AI Found a Bug!</div>
           <div className="text-red-100 text-[11px] leading-snug">
             Customers can't click the <strong>Check Pincode</strong> button because the WhatsApp chat icon is blocking it on mobile screens.
             <div className="mt-2 text-white font-semibold flex items-center gap-1.5 bg-black/20 p-1.5 rounded border border-red-500/30 w-max">
               <TrendingUp size={12} className="text-red-400" /> ₹24,500 in sales lost today.
             </div>
           </div>
         </div>
       </div>
    </div>
  </div>
);

const VoiceBotScreen = () => (
  <div className="h-full w-full bg-[#05050A] text-white relative flex flex-col overflow-hidden pt-12 pb-6">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[150%] h-[150%] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-600/20 via-[#05050A] to-[#05050A] opacity-80 pointer-events-none"></div>
    <div className="flex flex-col items-center z-10 mb-2 shrink-0">
      <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-blue-600 to-cyan-400 mb-3 flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.5)] relative">
        <div className="absolute inset-0 bg-white/20 rounded-full animate-ping opacity-50"></div>
        <Zap size={24} className="text-white relative z-10" />
      </div>
      <h2 className="text-2xl font-light tracking-wide mb-1">Canvas AI Support</h2>
      <span className="text-white/60 text-sm font-mono tracking-widest">01:24</span>
    </div>
    <div className="flex-1 w-full overflow-hidden relative z-10 flex flex-col justify-end px-5 pb-4">
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-[#05050A] to-transparent z-20 pointer-events-none"></div>
      <div className="space-y-4 text-sm leading-relaxed max-h-[200px] flex flex-col justify-end">
        <div className="animate-[fade-in-up_0.5s_ease-out_both]">
          <span className="font-semibold text-white/50 text-xs block mb-0.5">Canvas AI</span>
          <span className="text-white/70">I can help you track your order or process a return. What do you need?</span>
        </div>
        <div className="animate-[fade-in-up_1s_ease-out_both] text-right">
          <span className="font-semibold text-blue-400/70 text-xs block mb-0.5">You</span>
          <span className="text-blue-400">I need to check the status of my recent order.</span>
        </div>
        <div className="animate-[fade-in-up_1.5s_ease-out_both] relative">
          <span className="font-semibold text-white/50 text-xs flex items-center gap-1.5 mb-0.5">
            Canvas AI 
            <span className="flex gap-0.5 mt-0.5">
              <span className="w-1 h-1 bg-green-400 rounded-full animate-bounce"></span>
              <span className="w-1 h-1 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '100ms'}}></span>
              <span className="w-1 h-1 bg-green-400 rounded-full animate-bounce" style={{animationDelay: '200ms'}}></span>
            </span>
          </span>
          <span className="text-white">Sure, looking that up... Your Velocity Jacket is out for delivery today by 8 PM.<span className="inline-block w-1.5 h-3.5 bg-blue-500 ml-1 animate-pulse align-middle"></span></span>
        </div>
      </div>
    </div>
    <div className="z-10 px-6 shrink-0 mt-auto">
      <div className="grid grid-cols-3 gap-y-5 gap-x-4 mb-8">
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
            <MicOff size={22} className="text-white" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">mute</span>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
            <LayoutDashboard size={22} className="text-white" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">keypad</span>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
            <Volume2 size={22} className="text-blue-400" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">speaker</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 opacity-40">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center">
            <Plus size={22} className="text-white" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">add call</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 opacity-40">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center">
            <Video size={22} className="text-white" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">FaceTime</span>
        </div>
        <div className="flex flex-col items-center gap-1.5 opacity-40">
          <div className="w-[52px] h-[52px] rounded-full bg-white/10 backdrop-blur-xl border border-white/5 flex items-center justify-center">
            <Users size={22} className="text-white" />
          </div>
          <span className="text-[9px] font-medium text-white/70 tracking-wide">contacts</span>
        </div>
      </div>
      <div className="flex justify-center pb-2">
        <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.4)] cursor-pointer hover:bg-red-600 transition-colors">
          <PhoneOff size={28} className="text-white" />
        </div>
      </div>
    </div>
  </div>
);

// --- MAIN APPLICATION COMPONENT ---

export default function App() {
  const [data, setData] = useState(initialData);
  const [isEditingCMS, setIsEditingCMS] = useState(false);
  
  // Auth & Admin State
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // UI State
  const [currentPage, setCurrentPage] = useState('home');
  const [activeSolutionTab, setActiveSolutionTab] = useState(0);
  const [activePricingTab, setActivePricingTab] = useState('whatsapp');
  const [activeRoiTab, setActiveRoiTab] = useState(0);
  const [roiInputs, setRoiInputs] = useState({ traffic: 10000, aov: 4000, abandoned: 500, tickets: 300 });
  const [selectedPlanModalIndex, setSelectedPlanModalIndex] = useState(null);

  // --- FIREBASE LOGIC ---
  useEffect(() => {
    if (!auth) return;
    const initAuth = async () => {
      try {
        if (typeof window !== 'undefined' && window.__initial_auth_token) {
          try {
            await signInWithCustomToken(auth, window.__initial_auth_token);
          } catch (tokenError) {
            console.warn("Custom token failed (likely custom Firebase config). Falling back to anonymous auth.", tokenError);
            await signInAnonymously(auth);
          }
        } else {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("Authentication failed:", err);
      }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Sync live changes from Firestore
    if (!user || !db) return;
    const docRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'cms'), 'siteData');
    
    const unsubscribe = onSnapshot(docRef, 
      (snapshot) => {
        if (snapshot.exists()) {
          // Update live data with stored CMS content
          setData(snapshot.data());
        }
      }, 
      (error) => {
        console.error("Firestore sync error:", error);
        if (error.code === 'permission-denied' || error.message.includes('permission')) {
          console.warn(`
            ⚠️ FIREBASE PERMISSION ERROR: Your Firestore database rules are blocking access.
            To fix this, go to your Firebase Console -> Firestore Database -> Rules and set them to:
            
            rules_version = '2';
            service cloud.firestore {
              match /databases/{database}/documents {
                match /{document=**} {
                  allow read, write: if true;
                }
              }
            }
          `);
        }
      }
    );
    return () => unsubscribe();
  }, [user]);

  const handlePublish = async (newData) => {
    setData(newData); // Optimistic UI update
    setIsEditingCMS(false);
    
    // Persist to Cloud so it reflects properly in code across sessions
    if (user && db) {
      try {
        const docRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'cms'), 'siteData');
        await setDoc(docRef, newData);
      } catch (e) {
        console.error("Failed to save changes to cloud:", e);
        if (e.code === 'permission-denied' || e.message.includes('permission')) {
          setLoginError("Permission Denied: Please update your Firestore Security Rules to 'allow read, write: if true;'");
          setShowLoginModal(true);
        }
      }
    }
  };

  const handleAdminLogin = () => {
    if (adminPassword === 'admin') {
      setIsAdmin(true);
      setShowLoginModal(false);
      setAdminPassword('');
      setLoginError('');
    } else {
      setLoginError('Incorrect password');
    }
  };

  useEffect(() => {
    if(!isEditingCMS) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, isEditingCMS]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700&display=swap');
      body { background-color: #05050A; color: white; margin: 0; padding: 0; overflow-x: hidden; }
      @keyframes fade-in-up {
        0% { opacity: 0; transform: translateY(10px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes scroll-up-down {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-30%); }
      }
      @keyframes frantic-click {
        0% { top: 80%; left: 50%; opacity: 0; }
        15% { top: 225px; left: 195px; opacity: 1; }
        20% { top: 225px; left: 195px; transform: scale(0.85); }
        25% { top: 225px; left: 195px; transform: scale(1); }
        30% { top: 225px; left: 195px; transform: scale(0.85); }
        35% { top: 225px; left: 195px; transform: scale(1); }
        50% { top: 225px; left: 195px; opacity: 1; }
        70% { top: 90%; left: 50%; opacity: 0; }
        100% { top: 90%; left: 50%; opacity: 0; }
      }
      @keyframes cursor-move {
        0% { top: 60%; left: 30%; }
        25% { top: 40%; left: 60%; }
        50% { top: 30%; left: 40%; }
        75% { top: 50%; left: 70%; }
        100% { top: 60%; left: 30%; }
      }
      @keyframes wave {
        0%, 100% { transform: scaleY(0.5); }
        50% { transform: scaleY(1); }
      }
      @keyframes dash {
        to { stroke-dashoffset: 0; }
      }
      /* Custom Scrollbar for CMS */
      .scrollbar-hide::-webkit-scrollbar {
        width: 8px;
      }
      .scrollbar-hide::-webkit-scrollbar-track {
        background: transparent;
      }
      .scrollbar-hide::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
      }
      .scrollbar-hide::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const navigateToHomeSection = (sectionId) => {
    setCurrentPage('home');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if(el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // --- RENDER FULL PAGE CMS IF ACTIVE (Only accessible if Admin) ---
  if (isEditingCMS && isAdmin) {
    return <CMSEditor initialData={data} onPublish={handlePublish} onClose={() => setIsEditingCMS(false)} />;
  }

  // --- RENDER REGULAR WEBSITE FRONTEND ---
  return (
    <div className="min-h-screen w-full bg-[#05050A] text-white relative selection:bg-blue-500/30 overflow-x-hidden font-sans" style={{ fontFamily: "'Lexend', sans-serif" }}>
      {/* Global Background */}
      <div className="fixed top-[-20%] left-[-10%] w-[50vw] h-[50vw] bg-blue-700/20 rounded-full blur-[120px] pointer-events-none z-0"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[40vw] h-[40vw] bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none z-0"></div>

      {/* --- HEADER --- */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 border-b border-white/5 bg-[#05050A]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            onClick={() => setCurrentPage('home')}
            className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 tracking-tight cursor-pointer flex items-center"
          >
            {data.header.logoImage ? (
              <img src={data.header.logoImage} alt="Logo" className="h-8 object-contain" />
            ) : (
              <EText data={data} path="header.logo" />
            )}
          </div>
          
          <nav className="hidden md:flex gap-8 items-center">
            {/* Solutions Dropdown */}
            <div className="relative group py-2">
              <button className="text-sm font-medium text-white/70 hover:text-white flex items-center gap-1 transition-colors">
                Solutions <ChevronDown size={14} className="group-hover:rotate-180 transition-transform"/>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-[#0a0c10] border border-white/10 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 flex flex-col overflow-hidden backdrop-blur-xl">
                {data.solutions.map(sol => (
                  <button 
                    key={sol.id} 
                    onClick={() => setCurrentPage(sol.id)} 
                    className="px-4 py-3 text-left text-sm text-white/70 hover:text-white hover:bg-white/5 flex items-center gap-3 border-b border-white/5 last:border-0"
                  >
                    <IconRenderer name={sol.icon} className="w-4 h-4 text-blue-400" />
                    {sol.tabTitle}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => navigateToHomeSection('roi-calculator')} className="text-sm font-medium text-white/70 hover:text-white transition-colors">
              <EText data={data} path="header.nav.0" />
            </button>
            <button onClick={() => navigateToHomeSection('pricing')} className="text-sm font-medium text-white/70 hover:text-white transition-colors">
              <EText data={data} path="header.nav.1" />
            </button>
            
            <button className="bg-white/10 hover:bg-white/20 border border-white/10 px-5 py-2 rounded-full text-sm font-semibold transition-all">
              Login
            </button>
          </nav>
        </div>
      </header>

      {/* --- PAGE RENDERING LOGIC --- */}
      {currentPage === 'home' ? (
        <>
          {/* --- SECTION 1: HERO (Dashboard Mockup Right) --- */}
          <section className="relative z-10 pt-40 pb-20 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 min-h-[90vh]">
            <div className="flex-1 flex flex-col items-start gap-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold">
                 <Zap size={16} />
                 <EText data={data} path="hero.badge" />
              </div>
              <EText data={data} path="hero.title" tag="h1" className="text-5xl lg:text-7xl font-bold leading-tight" />
              <EText data={data} path="hero.subtitle" tag="p" className="text-lg text-white/60 max-w-xl leading-relaxed" />
              
              <div className="flex items-center gap-4 mt-4 w-full sm:w-auto">
                <button className="flex-1 sm:flex-none px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 text-white font-semibold shadow-[0_0_20px_rgba(37,99,235,0.4)] hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] transition-all">
                   <EText data={data} path="hero.cta1" />
                </button>
                <button className="flex-1 sm:flex-none px-8 py-4 rounded-full bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-all flex items-center justify-center gap-2">
                   <EText data={data} path="hero.cta2" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 flex justify-center lg:justify-end w-full perspective-1000">
               <DashboardMockup />
            </div>
          </section>

          {/* --- SECTION 2: OUR SOLUTIONS (Mockup Left, Tabs Right) --- */}
          <section id="solutions" className="relative z-10 py-24 px-6 max-w-7xl mx-auto border-t border-white/5">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">The Future of E-Commerce</h2>
              <p className="text-white/60">Hover over our solutions to see them in action, or click to explore.</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-16 items-center">
              {/* Mockup Display (Left Side) */}
              <div className="flex-1 flex justify-center lg:justify-start items-center min-h-[600px]">
                 <div className="relative">
                   <div className={`absolute inset-0 blur-[80px] rounded-full transition-colors duration-500 ${
                     activeSolutionTab === 0 ? 'bg-green-500/20' : 
                     activeSolutionTab === 1 ? 'bg-blue-500/20' : 
                     activeSolutionTab === 2 ? 'bg-red-500/20' : 'bg-purple-500/20'
                   }`}></div>
                   {activeSolutionTab === 1 ? (
                     <BrowserFrame className="transform transition-all duration-300">
                       <ThemeScreen />
                     </BrowserFrame>
                   ) : (
                     <PhoneFrame className="transform transition-all duration-300">
                       {activeSolutionTab === 0 && <WhatsAppScreen />}
                       {activeSolutionTab === 2 && <HeatmapScreen />}
                       {activeSolutionTab === 3 && <VoiceBotScreen />}
                     </PhoneFrame>
                   )}
                 </div>
              </div>

              {/* Interactive Tabs (Right Side) */}
              <div className="flex-1 flex flex-col gap-4">
                {data.solutions.map((sol, idx) => (
                  <div 
                    key={sol.id}
                    onMouseEnter={() => setActiveSolutionTab(idx)}
                    onClick={() => setCurrentPage(sol.id)}
                    className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 border group ${
                      activeSolutionTab === idx 
                        ? 'bg-white/10 border-blue-500/50 shadow-[0_0_30px_rgba(37,99,235,0.15)] scale-[1.02] transform z-10' 
                        : 'bg-white/[0.02] border-white/5 hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl transition-colors ${activeSolutionTab === idx ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/50'}`}>
                          <IconRenderer name={sol.icon} className="w-6 h-6" />
                        </div>
                        <EText data={data} path={`solutions.${idx}.tabTitle`} tag="h3" className="text-xl font-semibold" />
                      </div>
                      <ChevronRight className={`transition-all duration-300 ${activeSolutionTab === idx ? 'text-blue-400 translate-x-1 opacity-100' : 'text-white/20 opacity-0 group-hover:opacity-100 group-hover:translate-x-0'}`} />
                    </div>
                    
                    <div className={`grid transition-all duration-500 ease-in-out ${activeSolutionTab === idx ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}>
                      <div className="overflow-hidden">
                        <EText data={data} path={`solutions.${idx}.description`} tag="p" className="text-white/70 leading-relaxed mb-4" />
                        <ul className="space-y-2 mb-4">
                          {sol.features.map((feat, fIdx) => (
                            <li key={fIdx} className="flex items-center gap-2 text-sm text-white/80">
                               <CheckCircle2 size={14} className="text-blue-400" />
                               <EText data={data} path={`solutions.${idx}.features.${fIdx}`} />
                            </li>
                          ))}
                        </ul>
                        <span className="text-blue-400 text-sm font-semibold hover:underline">Explore full features →</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* --- SECTION 3: ROI CALCULATOR --- */}
          <section id="roi-calculator" className="relative z-10 py-24 px-6 bg-black/40 border-y border-white/5">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">Calculate Your ROI</h2>
                <p className="text-white/60">See exactly how much revenue you're leaving on the table.</p>
              </div>

              <GlassBox className="p-2 sm:p-8">
                <div className="flex flex-wrap gap-2 mb-8 bg-black/50 p-2 rounded-xl border border-white/10">
                  {['WhatsApp Recovery', 'Theme Boost', 'Heatmap UX', 'Voice Support'].map((tab, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setActiveRoiTab(idx)}
                      className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${activeRoiTab === idx ? 'bg-blue-600 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/5'}`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-12 items-center p-4">
                  <div className="space-y-6">
                    {activeRoiTab === 0 && (
                      <>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Abandoned Carts / Month</label>
                          <input type="number" value={roiInputs.abandoned} onChange={(e)=>setRoiInputs({...roiInputs, abandoned: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Average Order Value (₹)</label>
                          <input type="number" value={roiInputs.aov} onChange={(e)=>setRoiInputs({...roiInputs, aov: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                      </>
                    )}
                    {(activeRoiTab === 1 || activeRoiTab === 2) && (
                      <>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Monthly Traffic (Visitors)</label>
                          <input type="number" value={roiInputs.traffic} onChange={(e)=>setRoiInputs({...roiInputs, traffic: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Average Order Value (₹)</label>
                          <input type="number" value={roiInputs.aov} onChange={(e)=>setRoiInputs({...roiInputs, aov: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                      </>
                    )}
                    {activeRoiTab === 3 && (
                      <>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Support Tickets / Month</label>
                          <input type="number" value={roiInputs.tickets} onChange={(e)=>setRoiInputs({...roiInputs, tickets: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors" />
                        </div>
                        <div>
                          <label className="block text-sm text-white/70 mb-2">Cost Per Human Ticket (₹)</label>
                          <input type="number" value={400} readOnly className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/50 cursor-not-allowed" />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-blue-900/40 to-black rounded-2xl p-8 border border-blue-500/20 flex flex-col items-center justify-center text-center h-full relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDUiLz4KPC9zdmc+')] opacity-20"></div>
                    <div className="relative z-10">
                      <div className="text-blue-400 font-semibold mb-2">Estimated Monthly Lift</div>
                      <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200 mb-4">
                        ₹{ 
                          activeRoiTab === 0 ? ((roiInputs.abandoned * 0.15) * roiInputs.aov).toLocaleString(undefined, {maximumFractionDigits:0}) :
                          activeRoiTab === 1 ? ((roiInputs.traffic * 0.005) * roiInputs.aov).toLocaleString(undefined, {maximumFractionDigits:0}) :
                          activeRoiTab === 2 ? ((roiInputs.traffic * 0.002) * roiInputs.aov).toLocaleString(undefined, {maximumFractionDigits:0}) :
                          (roiInputs.tickets * 0.8 * 400).toLocaleString(undefined, {maximumFractionDigits:0})
                        }
                      </div>
                      <p className="text-white/60 text-sm">
                        {activeRoiTab === 0 && "Based on an average 15% recovery rate with Agentic AI."}
                        {activeRoiTab === 1 && "Based on a modest 0.5% conversion rate increase."}
                        {activeRoiTab === 2 && "Based on fixing UX drops for a 0.2% CR bump."}
                        {activeRoiTab === 3 && "Based on 80% automated resolution saving ₹400/ticket."}
                      </p>
                    </div>
                  </div>
                </div>
              </GlassBox>
            </div>
          </section>

          {/* --- SECTION 4: PRICING --- */}
          <section id="pricing" className="relative z-10 py-24 px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold mb-4">Simple, Scalable Pricing</h2>
              <p className="text-white/60">Choose the plan that fits your growth stage.</p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {data.solutions.map((sol) => (
                <button 
                  key={sol.id}
                  onClick={() => setActivePricingTab(sol.id)}
                  className={`py-2 px-6 rounded-full text-sm font-semibold transition-all ${
                    activePricingTab === sol.id 
                      ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {sol.tabTitle}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {data.pricing[activePricingTab].map((plan, idx) => (
                <GlassBox key={idx} className={`p-8 flex flex-col ${idx === 1 ? 'border-blue-500/50 shadow-[0_0_40px_rgba(37,99,235,0.15)] scale-105 z-10' : ''}`}>
                  {idx === 1 && <div className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full self-start mb-4 uppercase tracking-wider">Most Popular</div>}
                  <EText data={data} path={`pricing.${activePricingTab}.${idx}.name`} tag="h3" className="text-2xl font-bold mb-2" />
                  <div className="flex items-baseline gap-1 mb-4">
                    <EText data={data} path={`pricing.${activePricingTab}.${idx}.price`} tag="span" className="text-5xl font-black" />
                    {plan.price !== 'Custom' && <span className="text-white/50">/mo</span>}
                  </div>
                  <EText data={data} path={`pricing.${activePricingTab}.${idx}.desc`} tag="p" className="text-white/60 mb-8 pb-8 border-b border-white/10" />
                  
                  <ul className="space-y-4 mb-8 flex-1">
                    {plan.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-3">
                        <CheckCircle2 size={18} className="text-blue-400 shrink-0" />
                        <EText data={data} path={`pricing.${activePricingTab}.${idx}.features.${fIdx}`} tag="span" className="text-sm text-white/80" />
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-col gap-2 mt-auto">
                    <button className={`w-full py-4 rounded-xl font-semibold transition-all ${idx === 1 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white/5 hover:bg-white/10 text-white'}`}>
                      Get Started
                    </button>
                    <button 
                      onClick={() => setSelectedPlanModalIndex(idx)}
                      className="w-full py-3 rounded-xl font-medium transition-all text-white/70 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10"
                    >
                      Know More
                    </button>
                  </div>
                </GlassBox>
              ))}
            </div>
          </section>
        </>
      ) : (
        /* --- INNER PAGES VIEW --- */
        <section className="relative z-10 pt-32 pb-24 px-6 max-w-7xl mx-auto min-h-screen">
          <button 
            onClick={() => setCurrentPage('home')} 
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium mb-12 transition-colors w-max"
          >
            <ArrowLeft size={16} /> Back to Home
          </button>

          <div className="flex flex-col lg:flex-row gap-16 items-center">
            {/* Left Side: Text Data */}
            <div className="flex-1">
               <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold">
                  <IconRenderer name={data.solutions.find(s => s.id === currentPage)?.icon} className="w-4 h-4" />
                  <span>{data.solutions.find(s => s.id === currentPage)?.tabTitle}</span>
               </div>
               
               <EText data={data} path={`innerPages.${currentPage}.title`} tag="h1" className="text-4xl lg:text-5xl font-bold leading-tight mb-6" />
               <EText data={data} path={`innerPages.${currentPage}.description`} tag="p" className="text-lg text-white/60 mb-10 leading-relaxed" />

               <div className="space-y-8">
                 {data.innerPages[currentPage].features.map((feat, idx) => (
                   <div key={idx} className="flex gap-4">
                     <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                       <CheckCircle2 className="text-blue-400 w-6 h-6" />
                     </div>
                     <div>
                       <EText data={data} path={`innerPages.${currentPage}.features.${idx}.title`} tag="h3" className="text-xl font-semibold mb-2" />
                       <EText data={data} path={`innerPages.${currentPage}.features.${idx}.desc`} tag="p" className="text-white/60 leading-relaxed" />
                     </div>
                   </div>
                 ))}
               </div>

               <button className="mt-10 px-8 py-4 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors shadow-[0_0_20px_rgba(37,99,235,0.3)]">
                 Start Implementing Now
               </button>
            </div>

            {/* Right Side: Mockup */}
            <div className="flex-1 flex justify-center lg:justify-end items-center min-h-[600px] perspective-1000">
               <div className="relative">
                 <div className="absolute inset-0 bg-blue-600/20 blur-[100px] rounded-full pointer-events-none"></div>
                 {currentPage === 'themes' || currentPage === 'heatmaps' ? (
                   <BrowserFrame className="transform rotate-[2deg] hover:rotate-0 transition-transform duration-500 shadow-2xl">
                      {currentPage === 'themes' && <ThemeScreen />}
                      {currentPage === 'heatmaps' && <HeatmapScreen />}
                   </BrowserFrame>
                 ) : (
                   <PhoneFrame className="transform rotate-[2deg] hover:rotate-0 transition-transform duration-500 shadow-2xl">
                      {currentPage === 'whatsapp' && <WhatsAppScreen />}
                      {currentPage === 'voicebot' && <VoiceBotScreen />}
                   </PhoneFrame>
                 )}
               </div>
            </div>
          </div>
        </section>
      )}

      {/* --- SECTION 5: FOOTER --- */}
      <footer className="relative z-10 py-12 border-t border-white/5 bg-[#030305]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <div onClick={() => setCurrentPage('home')} className="font-bold text-white/80 cursor-pointer flex items-center">
            {data.header.logoImage ? (
              <img src={data.header.logoImage} alt="Logo" className="h-6 object-contain opacity-80 hover:opacity-100 transition-opacity" />
            ) : (
              <EText data={data} path="header.logo" />
            )}
          </div>
          <EText data={data} path="footer.text" tag="p" />
          <div className="flex gap-4 items-center">
            <span onClick={() => setShowLoginModal(true)} className="hover:text-white cursor-pointer flex items-center gap-1">
              <Lock size={12} /> Admin
            </span>
            <span className="hover:text-white cursor-pointer">Privacy</span>
            <span className="hover:text-white cursor-pointer">Terms</span>
          </div>
        </div>
      </footer>

      {/* --- PRICING DETAILED MODAL --- */}
      {selectedPlanModalIndex !== null && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity"
            onClick={() => setSelectedPlanModalIndex(null)}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-[#0b0c10] border border-white/10 rounded-2xl p-8 max-w-lg w-full shadow-2xl animate-[fade-in-up_0.2s_ease-out]">
            <button 
              onClick={() => setSelectedPlanModalIndex(null)}
              className="absolute top-5 right-5 text-white/50 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2 rounded-full"
            >
              <X size={20} />
            </button>
            
            <div className="mb-6 pr-10">
              <h3 className="text-2xl font-bold text-white mb-2">
                <EText data={data} path={`pricing.${activePricingTab}.${selectedPlanModalIndex}.name`} /> Plan Details
              </h3>
              <div className="text-3xl font-black text-blue-400 mb-2">
                <EText data={data} path={`pricing.${activePricingTab}.${selectedPlanModalIndex}.price`} />
              </div>
              <EText data={data} path={`pricing.${activePricingTab}.${selectedPlanModalIndex}.desc`} tag="p" className="text-white/60 text-sm" />
            </div>

            <div className="space-y-4 mb-8">
              <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider border-b border-white/10 pb-3">All Included Features</h4>
              <ul className="space-y-4 pt-2">
                {data.pricing[activePricingTab][selectedPlanModalIndex].features.map((feat, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="text-blue-400 shrink-0" />
                    <EText data={data} path={`pricing.${activePricingTab}.${selectedPlanModalIndex}.features.${fIdx}`} tag="span" className="text-white/90 text-sm font-medium" />
                  </li>
                ))}
                
                {/* Extended Generic Features for Modal detail effect */}
                <li className="flex items-center gap-3 opacity-70">
                  <CheckCircle2 size={18} className="text-white/30 shrink-0" />
                  <span className="text-white/80 text-sm">24/7 Dedicated Support</span>
                </li>
                <li className="flex items-center gap-3 opacity-70">
                  <CheckCircle2 size={18} className="text-white/30 shrink-0" />
                  <span className="text-white/80 text-sm">Access to Community Discord</span>
                </li>
                <li className="flex items-center gap-3 opacity-70">
                  <CheckCircle2 size={18} className="text-white/30 shrink-0" />
                  <span className="text-white/80 text-sm">Advanced Analytics Dashboard</span>
                </li>
              </ul>
            </div>

            <button 
              onClick={() => setSelectedPlanModalIndex(null)}
              className="w-full py-4 rounded-xl font-bold bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)]"
            >
              Close Details
            </button>
          </div>
        </div>
      )}

      {/* --- ADMIN LOGIN MODAL --- */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLoginModal(false)}></div>
          <div className="bg-[#0b0c10] border border-white/10 p-8 rounded-2xl w-full max-w-sm relative shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-[fade-in-up_0.2s_ease-out]">
            <button onClick={() => setShowLoginModal(false)} className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"><X size={20}/></button>
            <h3 className="text-2xl font-bold mb-6 text-white flex items-center gap-2"><Lock size={20} className="text-blue-500"/> Admin Access</h3>
            
            <input 
              type="password" 
              placeholder="Enter admin password" 
              className={`w-full bg-[#05050A] border ${loginError ? 'border-red-500/50' : 'border-white/10'} rounded-xl p-3 text-white mb-4 focus:border-blue-500 outline-none`}
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setLoginError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()}
            />
            
            {loginError && <p className="text-red-400 text-xs font-semibold mb-4">{loginError}</p>}
            
            <button onClick={handleAdminLogin} className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-[0_0_15px_rgba(37,99,235,0.3)]">
              Login
            </button>
          </div>
        </div>
      )}

      {/* --- FLOATING OPEN CMS BUTTON (Only for Logged-In Admin) --- */}
      {isAdmin && !isEditingCMS && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end">
          <button 
            onClick={() => setIsEditingCMS(true)}
            className="flex items-center gap-2 px-6 py-4 rounded-full shadow-2xl transition-all bg-[#111318] border border-blue-500/30 text-white hover:bg-white/10 hover:border-blue-500 hover:shadow-[0_0_30px_rgba(37,99,235,0.3)] group"
          >
            <Settings size={20} className="text-blue-400 group-hover:rotate-90 transition-transform duration-300" />
            <span className="font-bold tracking-wide text-sm">Open CMS Editor</span>
          </button>
          <button 
            onClick={() => setIsAdmin(false)}
            className="text-xs font-medium text-white/40 hover:text-white mr-4"
          >
            Log Out
          </button>
        </div>
      )}

    </div>
  );
}